import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

import { getOrgConfigPublic, SurveyConfiguration } from "@rubi/api-generated";

import { AuthStateContext } from "@/contexts/AuthContext";

interface RegistryContextType {
  registryForms: SurveyConfiguration[];
  loading: boolean;
  error: Error | null;
  // Remembers the entries table page so it survives navigating into an
  // entry and back. Kept separate from the reports list's page state.
  registryTablePage: number;
  setRegistryTablePage: Dispatch<SetStateAction<number>>;
}

export const RegistryContext = createContext<RegistryContextType>({
  registryForms: [],
  loading: false,
  error: null,
  registryTablePage: 0,
  setRegistryTablePage: () => {},
});

export const RegistryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [registryForms, setRegistryForms] = useState<SurveyConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [registryTablePage, setRegistryTablePage] = useState(0);
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgID = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";

  useEffect(() => {
    const fetchRegistryForms = async () => {
      if (!orgID) return;

      setLoading(true);
      try {
        const response = (await getOrgConfigPublic({ path: { org_id: orgID } }))
          .data!;
        const registryConfigs = response.surveys.filter(
          (survey) => survey.is_registry,
        );
        setRegistryForms(registryConfigs);
      } catch (error) {
        setError(error as Error);
        console.error("Failed to fetch registry forms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistryForms();
  }, [currentUser, orgID]);

  return (
    <RegistryContext.Provider
      value={{
        registryForms,
        loading,
        error,
        registryTablePage,
        setRegistryTablePage,
      }}
    >
      {children}
    </RegistryContext.Provider>
  );
};

export const useRegistry = () => useContext(RegistryContext);
